import { trackArtifactEdit } from "@/components/artifacts/view/pendingArtifactEdits.ts";
import { errorToast } from "@/components/ui/toast.tsx";
import { useRoutineMutations } from "@/hooks/useRoutines.ts";
import { routineErrorMessage } from "@/shared/routines.ts";
import type { RoutineResp } from "@/types/routines.ts";
import { useCallback, useState } from "react";

/**
 * Turning one routine on or off, wherever the switch is drawn.
 *
 * Which write that takes depends on where the routine came from: a routine the
 * user owns is a plain update, while a shipped one goes through the system
 * toggle, which the backend turns into a preferences override. That rule belongs
 * in one place — the routines screen, the canvas card and the omnibox card all
 * draw different controls over the same decision, and a second copy is a surface
 * where a system routine silently fails to stick.
 *
 * Refusals are surfaced here too, in the server's own words: a routine whose end
 * date has passed refuses to enable, and without the toast the switch would just
 * slide back with no explanation.
 *
 * Every write registers with {@link trackArtifactEdit}. The picker surfaces sit
 * next to a Done that POSTs and has the server read the enabled set straight
 * back, so a toggle still in flight when Done is clicked would otherwise lose the
 * race — the shorter server path wins and the agent resumes naming the routine
 * the user just switched off (the shape of BIG-6261). `fireArtifactAction`
 * already awaits `flushPendingArtifactEdits`; this is its other half. The
 * routines screen registers too and has no action to block, which costs nothing.
 */
export const useRoutineToggle = (routine: RoutineResp) => {
  const { updateRoutine, toggleSystemRoutine } = useRoutineMutations();
  const [isSaving, setIsSaving] = useState(false);

  const isSystem = routine.kind === "system";
  const systemId = routine.id;

  const setEnabled = useCallback(
    async (next: boolean) => {
      setIsSaving(true);
      try {
        await trackArtifactEdit(
          isSystem
            ? toggleSystemRoutine(systemId, next)
            : updateRoutine(routine.id, { enabled: next }),
        );
      } catch (err) {
        errorToast(routineErrorMessage(err));
      } finally {
        setIsSaving(false);
      }
    },
    [isSystem, systemId, routine.id, toggleSystemRoutine, updateRoutine],
  );

  return { setEnabled, isSaving };
};
